import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { BRAND } from "@/lib/brand";
import {
  Briefcase, MapPin, Clock, IndianRupee, ChevronRight, ArrowLeft,
  Upload, CheckCircle2, Building2, ArrowUpRight, FileText, Loader2,
  Search,
} from "lucide-react";
import { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const PUBLIC = `${process.env.REACT_APP_BACKEND_URL}/api/public`;

const ID_PROOFS = [
  { key: "aadhaar", label: "Aadhaar Card" },
  { key: "pan", label: "PAN Card" },
  { key: "voter_id", label: "Voter ID" },
  { key: "passport", label: "Passport" },
];

function Header() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-end">
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/careers/status" data-testid="careers-track-status" className="text-gray-600 hover:text-[#0F172A] flex items-center gap-1">
            <Search className="h-3.5 w-3.5" /> Track application
          </Link>
          <Link to="/login" className="font-medium text-[#0F172A] hover:underline flex items-center gap-1" data-testid="careers-employee-signin">
            Employee sign in <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-gray-200 mt-20 py-8">
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4 text-xs text-gray-500">
        <div>© {new Date().getFullYear()} {BRAND.fullName}. All rights reserved.</div>
        <div>{BRAND.tagline}</div>
      </div>
    </footer>
  );
}

export function CareersHome() {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${PUBLIC}/jobs`).then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const filtered = (jobs || []).filter((j) =>
    !q || `${j.title} ${j.department} ${j.location}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="careers-home">
      <Header />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        data-testid="careers-hero"
        style={{ background: "linear-gradient(135deg, #0a1e4d 0%, #0f2862 50%, #163a8a 100%)" }}
      >
        {/* Soft dot texture overlay (no logo watermark) */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 50%, white 0px, transparent 1px), radial-gradient(circle at 80% 80%, white 0px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 text-white">
          <div className="inline-block bg-white rounded-2xl shadow-2xl p-6 md:p-8 mb-8" data-testid="careers-hero-logo-card">
            <img
              src={BRAND.logoUrl}
              alt="Solemnity IT Healthcare Solutions Pvt Ltd · Delivers Quality"
              data-testid="careers-hero-logo"
              className="w-full max-w-md md:max-w-lg h-auto block"
            />
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold tracking-tight max-w-3xl text-white">
            Build the future of healthcare technology with us.
          </h1>
          <p className="mt-4 text-white/75 max-w-xl text-base md:text-lg">
            We engineer systems trusted by clinics, hospitals, and patients. Join a team
            where craft, compliance, and care meet.
          </p>
          <div className="mt-8 flex items-center gap-3 max-w-md">
            <div className="flex-1 relative">
              <Input
                data-testid="careers-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search roles, departments, locations…"
                className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
              />
            </div>
          </div>
          <div className="mt-6 flex items-center gap-6 text-white/75 text-sm">
            <div><span className="font-display font-bold text-[#F26B3A] text-2xl">{jobs?.length ?? "—"}</span> open roles</div>
            <div><span className="font-display font-bold text-white text-2xl">7</span> teams</div>
            <div><span className="font-display font-bold text-white text-2xl">3</span> locations</div>
          </div>
        </div>
      </section>

      {/* Job list */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-semibold text-[#0F172A]">Open positions</h2>
          <span className="text-xs text-gray-500">{filtered.length} role{filtered.length === 1 ? "" : "s"}</span>
        </div>
        {jobs === null ? (
          <div className="space-y-3">{Array.from({length:3}).map((_,i)=><Skeleton key={`job-skel-${i}`} className="h-24 rounded-lg" />)}</div>
        ) : filtered.length === 0 ? (
          <Card className="hr-card p-10 text-center text-sm text-gray-500" data-testid="careers-empty">
            No roles match your search. Try clearing the filter or check back soon.
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((j) => (
              <Link
                to={`/careers/${j.id}`}
                key={j.id}
                data-testid={`careers-job-${j.id}`}
                className="block group"
              >
                <Card className="hr-card p-5 hover:border-[#F26B3A] hover:shadow-md transition flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-[#F26B3A]/5 text-[#0F172A] grid place-items-center">
                      <Briefcase className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-display text-lg font-semibold text-[#0F172A] group-hover:text-[#1E88E5] transition">{j.title}</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                        <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{j.department}</span>
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{j.experience}</span>
                        <span className="flex items-center gap-1"><IndianRupee className="h-3 w-3" />{(j.salary_min/100000).toFixed(1)}–{(j.salary_max/100000).toFixed(1)} LPA</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">{j.openings} opening{j.openings === 1 ? "" : "s"}</span>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-[#0F172A] transition" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

export function CareersJob() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [resumeFileId, setResumeFileId] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // id_proofs: { aadhaar: {file_id, name}, pan: {...}, voter_id: {...}, passport: {...} }
  const [idProofs, setIdProofs] = useState({});
  const [proofUploading, setProofUploading] = useState(null); // which kind is in flight
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", experience: "", notice_period: "",
    current_company: "", current_salary: "", expected_salary: "", cover_letter: "",
  });

  useEffect(() => {
    axios.get(`${PUBLIC}/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setJob(false));
  }, [id]);

  const uploadResume = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${PUBLIC}/upload-resume`, fd);
      setResumeFileId(data.file_id);
      setResumeName(data.filename);
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadIdProof = async (kind, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error("File must be under 3MB"); return; }
    setProofUploading(kind);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await axios.post(`${PUBLIC}/upload-id-proof?kind=${kind}`, fd);
      setIdProofs((prev) => ({ ...prev, [kind]: { file_id: data.file_id, name: data.filename } }));
      toast.success(`${ID_PROOFS.find((p) => p.key === kind).label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setProofUploading(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const proofs = Object.fromEntries(
        Object.entries(idProofs).map(([k, v]) => [k, v.file_id])
      );
      await axios.post(`${PUBLIC}/apply`, {
        job_id: id,
        ...form,
        current_salary: Number(form.current_salary) || 0,
        expected_salary: Number(form.expected_salary) || 0,
        resume_file_id: resumeFileId,
        id_proofs: proofs,
      });
      setDone(true);
      toast.success("Application submitted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (job === false) {
    return <div className="min-h-screen grid place-items-center text-gray-500">Job not found.</div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="careers-job-page">
      <Header />

      <section className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/careers")} data-testid="back-to-careers" className="text-sm text-gray-500 hover:text-[#0F172A] flex items-center gap-1 mb-4">
          <ArrowLeft className="h-4 w-4" /> All roles
        </button>

        {!job ? <Skeleton className="h-40 rounded-lg" /> : done ? (
          <Card className="hr-card p-10 text-center" data-testid="application-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="font-display text-2xl font-semibold text-[#0F172A] mt-4">Application received</h1>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Thank you, {form.name.split(" ")[0]}. Our recruiting team will review your application for <b>{job.title}</b> and reach out within 5 business days.
            </p>
            <div className="mt-6 flex justify-center gap-2">
              <Button onClick={() => navigate("/careers")} className="bg-[#F26B3A] hover:bg-[#d85d2f]" data-testid="success-view-more-jobs">View more jobs</Button>
            </div>
          </Card>
        ) : (
          <>
            <Card className="hr-card p-6 mb-6">
              <h1 className="font-display text-3xl font-semibold text-[#0F172A]">{job.title}</h1>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500 mt-2">
                <span className="flex items-center gap-1"><Building2 className="h-4 w-4" />{job.department}</span>
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{job.location}</span>
                <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{job.experience}</span>
                <span className="flex items-center gap-1"><IndianRupee className="h-4 w-4" />₹{(job.salary_min/100000).toFixed(1)}–{(job.salary_max/100000).toFixed(1)} LPA</span>
              </div>
              <div className="mt-4 text-sm text-gray-700 leading-relaxed">
                We&apos;re looking for a <b>{job.title}</b> to join our {job.department} team. You&apos;ll work on healthcare-grade systems that
                serve clinicians and patients, partner with senior engineers, and ship work that genuinely improves lives.
                Bring {job.experience} of relevant experience, healthy curiosity, and an eye for craft.
              </div>
            </Card>

            <Card className="hr-card p-6">
              <h2 className="font-display text-xl font-semibold text-[#0F172A]">Apply for this role</h2>
              <p className="text-xs text-gray-500 mb-4">All fields required unless marked optional.</p>
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name"><Input data-testid="apply-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Email"><Input data-testid="apply-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Mobile"><Input data-testid="apply-mobile" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></Field>
                <Field label="Experience"><Input data-testid="apply-experience" required placeholder="e.g. 5 yrs" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} /></Field>
                <Field label="Current company (optional)"><Input data-testid="apply-current-company" value={form.current_company} onChange={(e) => setForm({ ...form, current_company: e.target.value })} /></Field>
                <Field label="Notice period (optional)"><Input data-testid="apply-notice" placeholder="e.g. 60 days" value={form.notice_period} onChange={(e) => setForm({ ...form, notice_period: e.target.value })} /></Field>
                <Field label="Current CTC INR (optional)"><Input data-testid="apply-current-ctc" type="number" value={form.current_salary} onChange={(e) => setForm({ ...form, current_salary: e.target.value })} /></Field>
                <Field label="Expected CTC INR (optional)"><Input data-testid="apply-expected-ctc" type="number" value={form.expected_salary} onChange={(e) => setForm({ ...form, expected_salary: e.target.value })} /></Field>
                <div className="md:col-span-2">
                  <Field label="Why are you a fit? (optional)">
                    <Textarea data-testid="apply-cover" rows={4} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Resume (PDF/DOC, max 5MB)</Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    <label data-testid="apply-resume-label" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50 text-sm">
                      <Upload className="h-4 w-4" />
                      {uploading ? "Uploading…" : resumeName ? "Replace resume" : "Upload resume"}
                      <input type="file" accept=".pdf,.doc,.docx" onChange={uploadResume} className="hidden" data-testid="apply-resume-input" />
                    </label>
                    {resumeName && (
                      <span className="flex items-center gap-1.5 text-xs text-emerald-700">
                        <FileText className="h-3.5 w-3.5" /> {resumeName}
                      </span>
                    )}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Proof of identity (optional · PDF/JPG/PNG · max 3MB each)</Label>
                  <p className="text-[11px] text-gray-500 mt-0.5">Upload any of the following. You only need one — additional uploads help speed up your onboarding later.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {ID_PROOFS.map((p) => {
                      const uploaded = idProofs[p.key];
                      const isBusy = proofUploading === p.key;
                      return (
                        <label
                          key={p.key}
                          data-testid={`apply-idproof-${p.key}-label`}
                          className="cursor-pointer flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-gray-200 hover:border-[#F26B3A] bg-white text-sm transition"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`h-7 w-7 rounded-md grid place-items-center ${uploaded ? "bg-emerald-100 text-emerald-700" : "bg-[#F26B3A]/5 text-[#0F172A]"}`}>
                              {uploaded ? <CheckCircle2 className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                            </div>
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800">{p.label}</div>
                              <div className="text-[10px] text-gray-500 truncate max-w-[180px]">
                                {isBusy ? "Uploading…" : uploaded ? uploaded.name : "Not uploaded"}
                              </div>
                            </div>
                          </div>
                          <span className="text-[11px] text-[#0F172A] font-medium shrink-0">
                            {uploaded ? "Replace" : "Upload"}
                          </span>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => uploadIdProof(p.key, e)}
                            className="hidden"
                            data-testid={`apply-idproof-${p.key}-input`}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-2 pt-2">
                  <Button data-testid="apply-submit" type="submit" disabled={submitting || uploading} className="h-11 bg-[#F26B3A] hover:bg-[#d85d2f] w-full md:w-auto">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
                  </Button>
                </div>
              </form>
            </Card>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gray-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
